/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useRef, useMemo } from 'react';
import { Stage, Layer, Image as KonvaImage } from 'react-konva';
import useImage from 'use-image';
import { RiArrowGoBackLine, RiArrowGoForwardLine } from '@remixicon/react';
import { stageFrameClass, toolApplyClass, toolCancelClass, toolChipClass } from './editorUi';

interface RotateToolProps {
  image: string;
  onApply: (newImage: string) => void;
  onClose: () => void;
}

export function RotateTool({ image, onApply, onClose }: RotateToolProps) {
  const [rotation, setRotation] = useState(0);
  // tenta carregar com crossOrigin (útil para evitar taint se o servidor permitir)
  const [imageElement] = useImage(image);
  const stageRef = useRef<any>(null);

  const normalizedRotation = ((rotation % 360) + 360) % 360;

  const stageSize = useMemo(() => {
    if (!imageElement) return { width: 400, height: 300, scale: 1 };
  
    const naturalW = imageElement.naturalWidth || imageElement.width;
    const naturalH = imageElement.naturalHeight || imageElement.height;
  
    const isRotated90 = normalizedRotation === 90 || normalizedRotation === 270;
    const width = isRotated90 ? naturalH : naturalW;
    const height = isRotated90 ? naturalW : naturalH;
  
    const maxWidth = window.innerWidth * 0.8;
    const maxHeight = window.innerHeight * 0.6;
  
    // Calcula o scale apenas para o preview, mas mantém as dimensões originais
    const previewScale = Math.min(maxWidth / width, maxHeight / height, 1);
    const previewWidth = Math.round(width * previewScale);
    const previewHeight = Math.round(height * previewScale);
  
    return { 
      width: previewWidth, 
      height: previewHeight, 
      scale: previewScale,
      originalWidth: width,
      originalHeight: height
    };
  }, [imageElement, normalizedRotation]);

  const rotateLeft = () => setRotation((r) => r - 90);
  const rotateRight = () => setRotation((r) => r + 90);

  const applyRotation = () => {
    if (!imageElement) return;

    try {
      // Cria um canvas com as dimensões originais da imagem
      const naturalW = imageElement.naturalWidth || imageElement.width;
      const naturalH = imageElement.naturalHeight || imageElement.height;
      
      const isRotated90 = normalizedRotation === 90 || normalizedRotation === 270;
      const canvasWidth = isRotated90 ? naturalH : naturalW;
      const canvasHeight = isRotated90 ? naturalW : naturalH;
      
      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) return;
      
      // Move o contexto para o centro e rotaciona
      ctx.translate(canvasWidth / 2, canvasHeight / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      
      // Desenha a imagem com as dimensões originais
      ctx.drawImage(
        imageElement,
        -naturalW / 2,
        -naturalH / 2,
        naturalW,
        naturalH
      );
      
      // Converte canvas para base64
      const dataURL = canvas.toDataURL('image/jpeg', 0.9);
      onApply(dataURL);
      onClose();
    } catch (error) {
      console.error('Erro ao rotacionar imagem:', error);
    }
  };



    return (
    <div className="w-full h-full flex flex-col items-center justify-center relative">
      <div className="w-full">
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div style={{ width: stageSize.width, height: stageSize.height }}>
              <Stage
                ref={stageRef}
                width={stageSize.width}
                height={stageSize.height}
                style={{ width: stageSize.width + 'px', height: stageSize.height + 'px' }}
                className={stageFrameClass}
              >
                <Layer>
                  {imageElement && (
                    <KonvaImage
                      image={imageElement}
                      x={stageSize.width / 2}
                      y={stageSize.height / 2}
                      width={(imageElement.naturalWidth || imageElement.width) * stageSize.scale}
                      height={(imageElement.naturalHeight || imageElement.height) * stageSize.scale}
                      offsetX={((imageElement.naturalWidth || imageElement.width) * stageSize.scale) / 2}
                      offsetY={((imageElement.naturalHeight || imageElement.height) * stageSize.scale) / 2}
                      rotation={rotation}
                    />
                  )}
                </Layer>
              </Stage>
            </div>
            
            <div className="flex gap-2">
              <button
                type="button"
                onClick={rotateLeft}
                className={toolChipClass}
              >
                <RiArrowGoBackLine className="h-3.5 w-3.5" />
                Esquerda
              </button>
              <button
                type="button"
                onClick={rotateRight}
                className={toolChipClass}
              >
                <RiArrowGoForwardLine className="h-3.5 w-3.5" />
                Direita
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 absolute bottom-0 left-1/2 -translate-x-1/2">
          <button
            type="button"
            onClick={onClose}
            className={toolCancelClass}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={applyRotation}
            className={toolApplyClass}
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}
